import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { issueRepo } from '@/core/data/issueRepo';
import { tagRepo } from '@/core/data/miscRepo';
import { useSession } from '@/core/session';
import { t } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { ISSUE_FIELDS, ISSUE_KINDS, ISSUE_KIND_OTHER, fieldFixRoom, fieldIssuesOn, filterIssueTags, fixFieldPlan, issueDetailCatalog, issueFieldsOn, issueKindsOn, makeIssueText, markFieldPlan, normalizeIssue, otherIssueCatalog, type IssueKind, type IssuePlan, type ItemIssue } from '@/core/lib/issues';
import { FlagIcon } from '@/ui/icons';
import { tagLabel } from '@/core/lib/tagLabel';
import { tagIdsOf } from '@/core/domain/types';

export interface Issues {
  on: boolean; rows: ItemIssue[]; allTags: string[];
  eventTags: (tagIds?: number[] | null, categoryId?: number | null) => string[];
  toggle: (eventId: string, text: string, on: boolean) => void;
}

export function useIssues(): Issues {
  const { session } = useSession();
  const qc = useQueryClient();
  const on = !!session;
  const q = useQuery({ queryKey: ['itemIssues'], queryFn: issueRepo.list, enabled: on });
  const tags = useQuery({ queryKey: ['tags'], queryFn: tagRepo.list, enabled: on });
  const set = useMutation({
    mutationFn: (a: { eventId: string; text: string; on: boolean }) => (a.on ? issueRepo.remove(a.eventId, a.text) : issueRepo.add(a.eventId, a.text, session!.user.id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['itemIssues'] }),
    onError: (e) => reportError('web.discover.issue', e),
  });
  return { on, rows: q.data ?? [], allTags: (tags.data ?? []).map(tagLabel),
    eventTags: (tagIds, categoryId) => tagIdsOf({ tagIds, categoryId })
      .map((id) => (tags.data ?? []).find((tag) => tag.id === id)).filter(Boolean).map((tag) => tagLabel(tag!)),
    toggle: (eventId, text, isOn) => {
    const clean = normalizeIssue(text); if (clean && session) set.mutate({ eventId, text: clean, on: isOn });
  } };
}

export function IssueButton({ issues, eventId, tagIds, categoryId, className }: {
  issues: Issues; eventId: string; tagIds?: number[] | null;
  categoryId?: number | null; className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [kind, setKind] = React.useState<IssueKind | null>(null);
  const [field, setField] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState('');
  const [tagPicking, setTagPicking] = React.useState(false);
  const [tagQuery, setTagQuery] = React.useState('');
  if (!issues.on) return null;
  const mine = issues.rows.filter((r) => r.eventId === eventId);
  const selected = (k: IssueKind, detail: string) => mine.some((r) => r.text === makeIssueText(k, detail));
  const close = () => { setOpen(false); setKind(null); setField(null); setDraft(''); setTagPicking(false); setTagQuery(''); };
  const choose = (k: IssueKind, detail: string) => { const text = makeIssueText(k, detail); issues.toggle(eventId, text, selected(k, detail)); close(); };
  const save = () => { const clean = normalizeIssue(draft); if (kind && clean) choose(kind, clean); };
  /* فهرست از هسته می‌آید و همیشه کامل است — نگاه کن به `ISSUE_FIELDS`. */
  const baseFields = ISSUE_FIELDS.map((f) => ({ value: f.value, label: t(f.labelKey) }));
  const options: [string, string][] = kind === 'duplicate'
    ? [['same_subject_different_time', t('issueDuplicateTime')], ['complete_duplicate', t('issueDuplicateComplete')]]
    : kind === 'other' ? otherIssueCatalog(issues.rows).map((x) => [x, x])
      : kind === 'inappropriate' ? issueDetailCatalog(mine, kind).map((x) => [x, x])
        : baseFields.map((x) => [x.value, x.label]);
  const tagChoices = filterIssueTags(kind === 'wrong' ? issues.eventTags(tagIds, categoryId) : issues.allTags, tagQuery);
  const customOther = otherIssueCatalog(issues.rows);
  const markedKinds = issueKindsOn(mine, eventId);
  /* «جا افتاده ..» و «غلط است ..» فیلد می‌گیرند، پس بعدشان یک قدمِ دیگر هم
     دارند: مقدارِ درست. بقیه‌ی دسته‌ها همان‌جا ثبت می‌شوند. */
  const fieldKind = kind === 'missed' || kind === 'wrong';
  const fieldsOn = kind && fieldKind ? issueFieldsOn(issues.rows, eventId, kind) : new Set<string>();
  // علامتِ کنارِ نامِ فیلد: هر سطری روی آن فیلد، با مقدار یا بی‌مقدار.
  const marked = (value: string) => (!kind ? false : fieldKind ? fieldsOn.has(value) : selected(kind, value));
  const fieldRows = kind && field ? fieldIssuesOn(issues.rows, eventId, kind, field) : [];
  const fieldLabel = baseFields.find((x) => x.value === field)?.label ?? field ?? '';
  // نقشه‌ی `core/lib/issues` را اجرا می‌کند — `toggle` با `on: true` یعنی بردار.
  const apply = (plan: IssuePlan) => {
    plan.remove.forEach((text) => issues.toggle(eventId, text, true));
    if (plan.add) issues.toggle(eventId, plan.add, false);
  };
  /* زدنِ نامِ فیلد همان‌جا ثبتش می‌کند و بعد می‌پرسد درستش را می‌دانی یا نه —
     کادرِ پایین اختیاری است و هیچ‌وقت شرطِ ثبت شدن نبوده. */
  const pickField = (value: string) => {
    if (!kind) return;
    apply(markFieldPlan(issues.rows, eventId, kind, value));
    setField(value);
  };
  const saveFix = () => {
    if (!kind || !field || !normalizeIssue(draft)) return;
    apply(fixFieldPlan(issues.rows, eventId, kind, field, draft));
    close();
  };
  return <>
    <button type="button" className={`issuebtn${mine.length ? ' on' : ''}${className ? ` ${className}` : ''}`}
      aria-label={t('issueReport')} title={t('issueReport')} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}>
      <FlagIcon size={16} filled={mine.length > 0} />
    </button>
    {open ? <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={(e) => { e.preventDefault(); e.stopPropagation(); close(); }}>
      <div className="card modal issue-list" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
        <p className="menu-title">{field ? t('issueSavedAsk') : fieldKind ? t('issuePickField') : t('issueReport')}</p>
        {/* ترتیبِ ریشه: دسته‌ها، بعد میان‌برهای ذخیره‌شده، و «چیزِ دیگری»
            همیشه آخر — نگاه کن به `ISSUE_KINDS` در هسته. */}
        {!kind ? <>{ISSUE_KINDS.map((x) => <button key={x.kind} type="button" className={`menu-item${markedKinds.has(x.kind) ? ' active' : ''}`}
          onClick={() => setKind(x.kind)}>{markedKinds.has(x.kind) ? '✓ ' : ''}{t(x.labelKey)}</button>)}
          {customOther.map((value) => <button key={`other:${value}`} type="button" className={`menu-item${selected('other', value) ? ' active' : ''}`}
            onClick={() => choose('other', value)}>{selected('other', value) ? '✓ ' : ''}{value}</button>)}
          <button type="button" className={`menu-item${markedKinds.has('other') ? ' active' : ''}`}
            onClick={() => setKind('other')}>{markedKinds.has('other') ? '✓ ' : ''}{t(ISSUE_KIND_OTHER.labelKey)}</button></> : <>
          {field ? <>
            {/* همین حالا ثبت شده — این سطرها هم بازخوردِ آن ثبت‌اند و هم راهِ
                پس گرفتنش. */}
            {fieldRows.map((r) => <button key={r.detail} type="button" className="menu-item active"
              onClick={() => choose(kind, r.detail)}>✓ {r.fix ?? fieldLabel}</button>)}
            <div className="menu-custom">
              <input className="input" autoFocus value={draft} placeholder={t('issueCorrectPh')}
                maxLength={fieldFixRoom(kind, field)}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveFix(); }} />
              {/* خاموش تا چیزی نوشته نشود: دکمه‌ای که با کادرِ خالی هم فعال
                  باشد، همان «حتماً یه چیزی بنویس»ِ قبلی را می‌رساند. */}
              <button type="button" className="btn fill" disabled={!normalizeIssue(draft)}
                onClick={saveFix}>{t('issueAddFix')}</button>
            </div>
            <button type="button" className="menu-item" onClick={close}>{t('doneCheck')}</button>
          </> : tagPicking ? <>
            <div className="menu-custom"><input className="input" autoFocus value={tagQuery} placeholder={t('search')}
              onChange={(e) => setTagQuery(e.target.value)} /></div>
            {tagChoices.map((label) => <button key={label} type="button" className={`menu-item${selected(kind, `tag:${label}`) ? ' active' : ''}`}
              onClick={() => choose(kind, `tag:${label}`)}>{selected(kind, `tag:${label}`) ? '✓ ' : ''}{label}</button>)}
            {kind === 'missed' && normalizeIssue(tagQuery) && !tagChoices.some((x) => x.toLocaleLowerCase() === tagQuery.trim().toLocaleLowerCase())
              ? <button type="button" className="menu-item" onClick={() => choose(kind, `tag:${normalizeIssue(tagQuery)}`)}>{t('save')}: {normalizeIssue(tagQuery)}</button> : null}
          </> : options.map(([value, label]) => <button key={value} type="button" className={`menu-item${marked(value) ? ' active' : ''}`}
            onClick={() => value === 'tag' ? setTagPicking(true) : fieldKind ? pickField(value) : choose(kind, value)}>{marked(value) ? '✓ ' : ''}{label}</button>)}
          {(kind === 'inappropriate' || kind === 'other') ? <div className="menu-custom">
            <input className="input" autoFocus value={draft} placeholder={t(kind === 'other' ? 'issueOtherPh' : 'issueWhyPh')}
              onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
            <button type="button" className="btn fill" disabled={!normalizeIssue(draft)} onClick={save}>{t('save')}</button>
          </div> : null}
          <button type="button" className="menu-item dimmed"
            onClick={() => field ? (setField(null), setDraft('')) : tagPicking ? (setTagPicking(false), setTagQuery('')) : setKind(null)}>{t('issueBack')}</button>
        </>}
      </div>
    </div> : null}
  </>;
}
